<script>
import { defineAsyncComponent, useTemplateRef } from 'vue';
import { useWindowSize } from '@vueuse/core';
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useTrack } from 'dashboard/composables';
import keyboardEventListenerMixins from 'shared/mixins/keyboardEventListenerMixins';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';

import ReplyToMessage from './ReplyToMessage.vue';
import AttachmentPreview from 'dashboard/components/widgets/AttachmentsPreview.vue';
import ReplyTopPanel from 'dashboard/components/widgets/WootWriter/ReplyTopPanel.vue';
import MessageTemplatePanel from 'dashboard/components/widgets/conversation/MessageTemplatePanel.vue';
import MessageTemplatesAPI from 'dashboard/api/messageTemplates';
import ConversationApi from 'dashboard/api/conversations';
import ReplyEmailHead from './ReplyEmailHead.vue';
import ReplyBottomPanel from 'dashboard/components/widgets/WootWriter/ReplyBottomPanel.vue';
import CopilotReplyBottomPanel from 'dashboard/components/widgets/WootWriter/CopilotReplyBottomPanel.vue';
import ArticleSearchPopover from 'dashboard/routes/dashboard/helpcenter/components/ArticleSearch/SearchPopover.vue';
import CopilotEditorSection from './CopilotEditorSection.vue';
import MessageSignatureMissingAlert from './MessageSignatureMissingAlert.vue';
import ReplyBoxBanner from './ReplyBoxBanner.vue';
import ConversationViewersBanner from './ConversationViewersBanner.vue';
import QuotedEmailPreview from './QuotedEmailPreview.vue';
import { REPLY_EDITOR_MODES } from 'dashboard/components/widgets/WootWriter/constants';
import TasksAPI from 'dashboard/api/tasks';
import WootMessageEditor from 'dashboard/components/widgets/WootWriter/Editor.vue';
import AudioRecorder from 'dashboard/components/widgets/WootWriter/AudioRecorder.vue';
import { AUDIO_FORMATS, MESSAGE_TYPE } from 'shared/constants/messages';
import { isChannelDown, networkBrand } from 'dashboard/helper/channelDownHelper';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { CMD_AI_ASSIST } from 'dashboard/helper/commandbar/events';
import {
  getMessageVariables,
  getUndefinedVariablesInMessage,
  replaceVariablesInMessage,
} from '@chatwoot/utils';
import WhatsappTemplates from './WhatsappTemplates/Modal.vue';
import ContentTemplates from './ContentTemplates/ContentTemplatesModal.vue';
import { MESSAGE_MAX_LENGTH } from 'shared/helpers/MessageTypeHelper';
import inboxMixin, { INBOX_FEATURES } from 'shared/mixins/inboxMixin';
import { trimContent, debounce, getRecipients } from '@chatwoot/utils';
import wootConstants from 'dashboard/constants/globals';
import {
  extractQuotedEmailText,
  buildQuotedEmailHeader,
  truncatePreviewText,
  appendQuotedTextToMessage,
} from 'dashboard/helper/quotedEmailHelper';
import {
  CONVERSATION_EVENTS,
  CAPTAIN_EVENTS,
} from '../../../helper/AnalyticsHelper/events';
import fileUploadMixin from 'dashboard/mixins/fileUploadMixin';
import {
  appendSignature,
  removeSignature,
  getEffectiveChannelType,
} from 'dashboard/helper/editorHelper';
import { useCopilotReply } from 'dashboard/composables/useCopilotReply';
import { useKbd } from 'dashboard/composables/utils/useKbd';
import { isFileTypeAllowedForChannel } from 'shared/helpers/FileHelper';

import { LOCAL_STORAGE_KEYS } from 'dashboard/constants/localStorage';
import { LocalStorage } from 'shared/helpers/localStorage';
import { emitter } from 'shared/helpers/mitt';
const EmojiInput = defineAsyncComponent(
  () => import('shared/components/emoji/EmojiInput.vue')
);

// Presentación obligatoria a cliente nuevo (Victor 2026-05-23). Partes FIJAS
// (todo menos el nombre del agente). Si el agente borra cualquiera de estas, se
// muestra la advertencia roja y se restaura. Debe coincidir con la plantilla
// fija del backend (MessageTemplatesController::PRESENTATION_BODY).
const PRESENTATION_PREFIX =
  'Estimado cliente, usted se comunica con Victor Guzmán Fotografía 📸, le asiste';
const PRESENTATION_SUFFIX =
  'Para nosotros es más que un placer atenderle 🥰. Déjenos saber su nombre y apellido para agregarlo a nuestra base de datos.';

// @mención → tarea (Victor 2026-06-09): SOLO estos usuarios pueden asignar tareas
// mencionando a un agente en una nota. Victor Guzmán (1 y 3), Gabriela Abreu (41),
// Karina (6), Kemely (4), Rubí (19). El resto puede mencionar (notifica) pero NO
// genera tarea. Espejo del whitelist backend AgentPermissions.TASK_CREATOR_USER_IDS.
const MENTION_TASK_ASSIGNER_IDS = [1, 3, 4, 6, 19, 41];

export default {
  components: {
    ArticleSearchPopover,
    AttachmentPreview,
    AudioRecorder,
    ReplyBoxBanner,
    ConversationViewersBanner,
    EmojiInput,
    MessageSignatureMissingAlert,
    MessageTemplatePanel,
    ReplyBottomPanel,
    ReplyEmailHead,
    ReplyToMessage,
    ReplyTopPanel,
    ContentTemplates,
    WhatsappTemplates,
    WootMessageEditor,
    QuotedEmailPreview,
    CopilotEditorSection,
    CopilotReplyBottomPanel,
  },
  mixins: [inboxMixin, fileUploadMixin, keyboardEventListenerMixins],
  props: {
    popOutReplyBox: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update:popOutReplyBox'],
  setup() {
    const {
      uiSettings,
      isEditorHotKeyEnabled,
      fetchSignatureFlagFromUISettings,
      setQuotedReplyFlagForInbox,
      fetchQuotedReplyFlagFromUISettings,
    } = useUISettings();

    const replyEditor = useTemplateRef('replyEditor');
    const copilot = useCopilotReply();
    const shortcutKey = useKbd(['$mod', '+', 'enter']);
    const { width: windowWidth } = useWindowSize();

    return {
      uiSettings,
      isEditorHotKeyEnabled,
      fetchSignatureFlagFromUISettings,
      setQuotedReplyFlagForInbox,
      fetchQuotedReplyFlagFromUISettings,
      replyEditor,
      copilot,
      shortcutKey,
      windowWidth,
    };
  },
  data() {
    return {
      message: '',
      // @mención → tarea (Victor 2026-06-09): vencimiento y prioridad de la tarea
      // que se crea al mencionar un agente en una nota. La tarjeta solo aparece
      // para los usuarios autorizados (MENTION_TASK_ASSIGNER_IDS) cuando la nota
      // contiene una mención. 'hoy' | 'manana' | '3dias' · 'normal'|'alta'|'urgente'.
      mentionTaskDueKey: 'hoy',
      mentionTaskPriority: 'normal',
      // Auto-corregir al pausar (Victor 2026-06-05): guarda el último resultado de
      // la IA para no re-corregir en bucle (al aplicar el texto vuelve a disparar el
      // watcher). Ver runAutoCorrect().
      lastAutoCorrected: '',
      // HUD de auto-corrección (Victor 2026-06-05 · P4): cuántas correcciones hizo
      // la IA en la última pasada, clasificadas en gramática/tildes/mayúsculas. Se
      // calcula diferenciando el texto antes/después. total=0 → "activa" sin HUD.
      autoCorrectStats: { grammar: 0, accents: 0, caps: 0, total: 0 },
      // Presentación obligatoria a cliente nuevo (Victor 2026-05-23): conv id
      // para la que ya se manejó/envió la presentación (no re-rellenar).
      presentationHandledFor: null,
      presentationWarning: '',
      // Saludo obligatorio: el backend decide si el cliente es NUEVO EN LA
      // SUCURSAL y el inbox NO es personal (Victor 2026-05-24). Hasta que
      // responda, queda false → no se precarga nada.
      greetingEligible: false,
      // Saludo de presentación DIARIO (Victor 2026-06-04): el backend dice si es
      // el primer mensaje del día con el cliente (no personal, sin respuesta de
      // agente hoy). Se precarga editable (Propuesta 1), NO de solo lectura.
      dailyGreetingEligible: false,
      // Datos del Responsable para el saludo del día (Victor 2026-06-05 · P4):
      // { name, gender:'f'|'m', is_me } o null si el cliente no tiene encargada.
      dailyGreetingEncargado: null,
      // Presentación obligatoria del rol Fotógrafo / Video (Victor 2026-06-07):
      // el backend decide si el usuario es fotógrafo Y el cliente aún no fue
      // presentado. Mientras sea true, una tarjeta cubre el cuadro de respuesta y
      // bloquea el chat hasta enviar el saludo fijo (una sola vez por cliente).
      presentationRequired: false,
      presentationText: '',
      presentationSending: false,
      inReplyTo: {},
      isFocused: false,
      showEmojiPicker: false,
      attachedFiles: [],
      isRecordingAudio: false,
      recordingAudioState: '',
      recordingAudioDurationText: '',
      replyType: REPLY_EDITOR_MODES.REPLY,
      bccEmails: '',
      ccEmails: '',
      toEmails: '',
      doAutoSaveDraft: () => {},
      showWhatsAppTemplatesModal: false,
      showContentTemplatesModal: false,
      updateEditorSelectionWith: '',
      undefinedVariableMessage: '',
      showMentions: false,
      showUserMentions: false,
      showCannedMenu: false,
      showTemplatePanel: false,
      showVariablesMenu: false,
      newConversationModalActive: false,
      showArticleSearchPopover: false,
      hasRecordedAudio: false,
      // Notas de voz Propuesta 2 (Victor 2026-06-02): pausa durante la grabación
      // y enviar-tras-detener en un solo toque.
      isAudioPaused: false,
      sendAfterRecord: false,
      copilotAcceptedMessages: {},
    };
  },
  computed: {
    // Auto-corregir al pausar: DESACTIVADO globalmente para todo el equipo
    // (Victor 2026-06-09). La corrección ahora es solo manual desde el menú
    // Victor AI (CopilotMenuBar). Ningún agente recibe autocorrección al pausar.
    autoCorrectEnabled() {
      return false;
    },
    // Celular (Victor 2026-06-02): al abrir un chat NO auto-enfocar el editor
    // (para que no salte el teclado y se vea la conversación completa). El foco
    // ocurre solo cuando el usuario toca la barra de escribir.
    isMobile() {
      return this.windowWidth < 768;
    },
    ...mapGetters({
      currentChat: 'getSelectedChat',
      messageSignature: 'getMessageSignature',
      currentUser: 'getCurrentUser',
      lastEmail: 'getLastEmailInSelectedChat',
      globalConfig: 'globalConfig/get',
      accountId: 'getCurrentAccountId',
      isFeatureEnabledonAccount: 'accounts/isFeatureEnabledonAccount',
    }),
    currentContact() {
      const senderId = this.currentChat?.meta?.sender?.id;
      if (!senderId) return {};
      return this.$store.getters['contacts/getContact'](senderId);
    },
    // Anti-choque por PRESENCIA · 2026-05-19 (Victor): NO depende de asignación.
    // Se bloquea solo si OTRO agente está DENTRO del chat ahora mismo (el
    // "holder" = el que entró primero). Si nadie está adentro → habilitado para
    // todos, aunque tenga responsable. Cuando el holder sale → se libera solo.
    // Solo aplica a sucursales conectadas (SDO+SDE) · Personal Privado no.
    connectedBranchIds() {
      const acc = this.$store.getters['accounts/getAccount'](this.accountId);
      const ids = acc?.settings?.connected_branch_ids;
      return Array.isArray(ids) ? ids : [];
    },
    sharedViewActive() {
      return this.connectedBranchIds.length >= 2;
    },
    isConnectedConversation() {
      const bid = this.currentChat?.branch_id;
      return !!bid && this.connectedBranchIds.includes(bid);
    },
    // Anti-choque por presencia RETIRADO · 2026-05-26 (Victor): la lógica de
    // "holder" era inconsistente (sacaba a quien entró primero y etiquetaba como
    // "responsable" a quien solo cubría). Ya NO se bloquea a nadie a solo-nota
    // por presencia: cualquiera puede responder y solo se muestra el aviso suave
    // (ConversationViewersBanner) de quién más está viendo el chat.
    isBlockedByConnectedView() {
      return false;
    },
    shouldShowReplyToMessage() {
      return (
        this.inReplyTo?.id &&
        !this.isPrivate &&
        this.inboxHasFeature(INBOX_FEATURES.REPLY_TO) &&
        !this.is360DialogWhatsAppChannel
      );
    },
    showWhatsappTemplates() {
      // We support templates for API channels if someone updates templates manually via API
      // That's why we don't explicitly check for channel type here
      const templates = this.$store.getters['inboxes/getWhatsAppTemplates'](
        this.inboxId
      );
      return !!(templates && templates.length) && !this.isPrivate;
    },
    showContentTemplates() {
      return this.isATwilioWhatsAppChannel && !this.isPrivate;
    },
    isPrivate() {
      if (
        this.currentChat.can_reply ||
        this.isAWhatsAppChannel ||
        this.isAPIInbox
      ) {
        return this.isOnPrivateNote;
      }
      return true;
    },
    isReplyRestricted() {
      return (
        !this.currentChat?.can_reply &&
        !(this.isAWhatsAppChannel || this.isAPIInbox)
      );
    },
    inboxId() {
      return this.currentChat.inbox_id;
    },
    inbox() {
      const stored = this.$store.getters['inboxes/getInbox'](this.inboxId);
      // Vista compartida (Victor 2026-05-26): si el agente NO es miembro del
      // inbox y su sucursal no está en accessible_branch_ids, la policy no
      // carga ese inbox en su store → llega {} → channel_type undefined →
      // showFileUpload=false → desaparecen el clip y el micrófono aunque SÍ
      // pueda ver/responder la conversación. El payload de la conversación ya
      // trae el tipo de canal en meta.channel, así que lo usamos de respaldo
      // para que los íconos de adjuntar/audio aparezcan siempre.
      if (!stored?.id && this.currentChat?.meta?.channel) {
        return { channel_type: this.currentChat.meta.channel };
      }
      return stored;
    },
    messagePlaceHolder() {
      // M v3 · M1.B: si la conv está siendo atendida por otro agente, mensaje
      // explicativo en lugar de los placeholders genéricos de canal.
      if (this.isAttendedByOtherAgent) {
        return `🙋 ${this.attendingByName} está atendiendo · solo el atendedor o el responsable pueden responder`;
      }
      // M v5 · M1.B: ex-atendedor bloqueado tras reclaim del responsable.
      if (this.isLockedByReclaim) {
        return `🙋 ${this.reclaimedByName} retomó la conversación · ya no puedes responder`;
      }
      if (this.isEditorDisabled) {
        if (this.isAWhatsAppChannel) {
          return this.$t('CONVERSATION.FOOTER.MESSAGING_RESTRICTED_WHATSAPP');
        }
        if (this.isAPIInbox) {
          return this.$t('CONVERSATION.FOOTER.MESSAGING_RESTRICTED_API');
        }
        return this.$t('CONVERSATION.FOOTER.MESSAGING_RESTRICTED');
      }
      if (this.isPrivate) {
        return this.$t('CONVERSATION.FOOTER.PRIVATE_MSG_INPUT');
      }
      // Celular (Victor 2026-06-02): placeholder corto para que no se corte en
      // 2 líneas. En PC/laptop se mantiene el hint del "/" Salesbot.
      if (this.isMobile) {
        return this.$t('CONVERSATION.FOOTER.MSG_INPUT_SHORT');
      }
      return this.$t('CONVERSATION.FOOTER.MSG_INPUT');
    },
    isMessageLengthReachingThreshold() {
      return this.message.length > this.maxLength - 50;
    },
    charactersRemaining() {
      return this.maxLength - this.message.length;
    },
    isReplyButtonDisabled() {
      if (this.isEditorDisabled) return true;
      if (this.isATwitterInbox) return true;
      if (this.hasAttachments || this.hasRecordedAudio) return false;

      return (
        this.isMessageEmpty ||
        this.message.length === 0 ||
        this.message.length > this.maxLength
      );
    },
    sender() {
      return {
        name: this.currentUser.name,
        thumbnail: this.currentUser.avatar_url,
      };
    },
    conversationType() {
      const { additional_attributes: additionalAttributes } = this.currentChat;
      const type = additionalAttributes ? additionalAttributes.type : '';
      return type || '';
    },
    maxLength() {
      if (this.isPrivate) {
        return MESSAGE_MAX_LENGTH.GENERAL;
      }
      if (this.isAFacebookInbox) {
        return MESSAGE_MAX_LENGTH.FACEBOOK;
      }
      if (this.isAnInstagramChannel) {
        return MESSAGE_MAX_LENGTH.INSTAGRAM;
      }
      if (this.isATiktokChannel) {
        return MESSAGE_MAX_LENGTH.TIKTOK;
      }
      if (this.isATwilioWhatsAppChannel) {
        return MESSAGE_MAX_LENGTH.TWILIO_WHATSAPP;
      }
      if (this.isAWhatsAppCloudChannel) {
        return MESSAGE_MAX_LENGTH.WHATSAPP_CLOUD;
      }
      if (this.isASmsInbox) {
        return MESSAGE_MAX_LENGTH.TWILIO_SMS;
      }
      if (this.isAnEmailChannel) {
        return MESSAGE_MAX_LENGTH.EMAIL;
      }
      if (this.isATwilioSMSChannel) {
        return MESSAGE_MAX_LENGTH.TWILIO_SMS;
      }
      if (this.isAWhatsAppChannel) {
        return MESSAGE_MAX_LENGTH.WHATSAPP_CLOUD;
      }
      return MESSAGE_MAX_LENGTH.GENERAL;
    },
    showFileUpload() {
      return (
        this.isAWebWidgetInbox ||
        this.isAFacebookInbox ||
        this.isAWhatsAppChannel ||
        this.isAPIInbox ||
        this.isAnEmailChannel ||
        this.isASmsInbox ||
        this.isATelegramChannel ||
        this.isALineChannel ||
        this.isAnInstagramChannel ||
        this.isATiktokChannel
      );
    },
    replyButtonLabel() {
      let sendMessageText = this.$t('CONVERSATION.REPLYBOX.SEND');
      if (this.isPrivate) {
        sendMessageText = this.$t('CONVERSATION.REPLYBOX.CREATE');
      }
      const keyLabel = this.isEditorHotKeyEnabled('cmd_enter')
        ? `(${this.shortcutKey})`
        : '(↵)';
      return `${sendMessageText} ${keyLabel}`;
    },
    replyBoxClass() {
      return {
        'is-private': this.isPrivate,
        'is-focused': this.isFocused || this.hasAttachments,
      };
    },
    hasAttachments() {
      return this.attachedFiles.length;
    },
    showAudioRecorder() {
      return !this.isOnPrivateNote && this.showFileUpload;
    },
    showAudioRecorderEditor() {
      return this.showAudioRecorder && this.isRecordingAudio;
    },
    isOnPrivateNote() {
      return this.replyType === REPLY_EDITOR_MODES.NOTE;
    },
    // @mención → tarea (Victor 2026-06-09) ───────────────────────────
    // ¿El usuario actual puede asignar tareas mencionando agentes? (los 5)
    canAssignMentionTask() {
      return MENTION_TASK_ASSIGNER_IDS.includes(this.currentUser?.id);
    },
    // ¿La nota actual contiene al menos una mención de agente?
    noteHasMention() {
      return /mention:\/\/user\/\d+/.test(this.message || '');
    },
    // Muestra la tarjeta de tarea: solo en nota, autorizado y con mención.
    showMentionTaskCard() {
      return (
        this.isOnPrivateNote &&
        this.canAssignMentionTask &&
        this.noteHasMention
      );
    },
    // Nombres de los agentes mencionados (para el texto de la tarjeta).
    mentionedAgentNames() {
      const names = [
        ...(this.message || '').matchAll(/\[([^\]]+)\]\(mention:\/\/user\/\d+\//g),
      ].map(m => m[1].replace(/^@/, ''));
      return [...new Set(names)];
    },
    isOnExpandedLayout() {
      const {
        LAYOUT_TYPES: { CONDENSED },
      } = wootConstants;
      const { conversation_display_type: conversationDisplayType = CONDENSED } =
        this.uiSettings;
      return conversationDisplayType !== CONDENSED;
    },
    isMessageEmpty() {
      if (!this.message) {
        return true;
      }
      return !this.message.trim().replace(/\n/g, '').length;
    },
    showReplyHead() {
      return !this.isOnPrivateNote && this.isAnEmailChannel;
    },
    enableMultipleFileUpload() {
      return (
        this.isAnEmailChannel ||
        this.isAWebWidgetInbox ||
        this.isAPIInbox ||
        this.isAWhatsAppChannel ||
        this.isATelegramChannel
      );
    },
    isSignatureEnabledForInbox() {
      return !this.isPrivate && this.sendWithSignature;
    },
    isSignatureAvailable() {
      return !!this.messageSignature;
    },
    sendWithSignature() {
      return this.fetchSignatureFlagFromUISettings(this.channelType);
    },
    conversationId() {
      return this.currentChat.id;
    },
    conversationIdByRoute() {
      return this.conversationId;
    },
    // ─── Presentación obligatoria a cliente nuevo (Victor 2026-05-23) ───
    // "Cliente nuevo" = el equipo nunca le ha enviado un mensaje (0 salientes).
    teamHasReplied() {
      const msgs = this.currentChat?.messages || [];
      return msgs.some(m => m.message_type === MESSAGE_TYPE.OUTGOING);
    },
    // Super admin (Victor, Gabriela Abreu): supervisan, no atienden — no se les
    // precarga ningún saludo automático en el cuadro de respuesta (Victor
    // 2026-06-06). Espejo de la verificación super_admin del account_user.
    isSuperAdmin() {
      const accInfo = this.currentUser?.accounts?.find(
        a => Number(a.id) === Number(this.accountId)
      );
      return accInfo?.super_admin === true;
    },
    isNewClientConversation() {
      return (
        this.greetingEligible &&
        !this.isOnPrivateNote &&
        !!this.currentChat?.id &&
        !this.teamHasReplied &&
        this.presentationHandledFor !== this.currentChat.id
      );
    },
    presentationMessage() {
      const agent = this.currentUser?.name || '';
      return `${PRESENTATION_PREFIX} ${agent}. ${PRESENTATION_SUFFIX}`;
    },
    // Saludo del día según la hora + presentación obligatoria (Victor 2026-06-05
    // · P4). El agente conectado se presenta y avisa que la encargada personal no
    // está, pero sigue pendiente. 3 variantes según el Responsable del cliente.
    dailyGreetingSaludo() {
      const h = new Date().getHours();
      if (h < 12) return 'Buenos días';
      if (h < 19) return 'Buenas tardes';
      return 'Buenas noches';
    },
    dailyGreetingMessage() {
      const agent = this.currentUser?.available_name || this.currentUser?.name || '';
      const saludo = this.dailyGreetingSaludo;
      const enc = this.dailyGreetingEncargado;

      // ③ Red de seguridad: cliente sin encargada/Responsable asignado.
      if (!enc?.name) {
        return `¡${saludo}! 🌞📷 Le saluda ${agent}, del equipo de Víctor Guzmán Fotografía. Será un gusto darle seguimiento hoy y ayudarle en lo que necesite 😊`;
      }

      const art = enc.gender === 'm' ? 'su encargado' : 'su encargada';
      const pron = enc.gender === 'm' ? 'él' : 'ella';

      // ② La propia encargada abre el chat de su cliente: se presenta sin ausencia.
      if (enc.is_me) {
        return `¡${saludo}! 🌞 Le saluda ${enc.name} 📷, ${art} personal en Víctor Guzmán Fotografía. Será un gusto seguir acompañándole hoy y ayudarle en lo que necesite 😊`;
      }

      // ① Caso normal · relevo: la encargada no está y otro agente cubre
      // (Victor 2026-06-06 · redacción aprobada). Presenta a la encargada personal,
      // avisa la ausencia y cierra con una pregunta para que el cliente continúe.
      // La frase "no se encuentra disponible en este momento" es la firma que el
      // backend usa para no repetir el saludo de relevo más de 1 vez al día.
      return `¡${saludo}! 🌞📷 Soy ${agent}, de Víctor Guzmán Fotografía. ${enc.name}, ${art} personal, no se encuentra disponible en este momento, así que en lo que ${pron} regresa quedo a cargo de su atención con mucho gusto. Cuénteme, ¿en qué le puedo ayudar hoy? 😊`;
    },
    editorStateId() {
      return `draft-${this.conversationIdByRoute}-${this.replyType}`;
    },
    audioRecordFormat() {
      if (this.isAWhatsAppChannel || this.isATelegramChannel) {
        return AUDIO_FORMATS.MP3;
      }
      if (this.isAPIInbox) {
        return AUDIO_FORMATS.MP3;
      }
      return AUDIO_FORMATS.WAV;
    },
    messageVariables() {
      const variables = getMessageVariables({
        conversation: this.currentChat,
        contact: this.currentContact,
        inbox: this.inbox,
      });
      return variables;
    },
    connectedPortalSlug() {
      const { help_center: portal = {} } = this.inbox;
      const { slug = '' } = portal;
      return slug;
    },
    isQuotedEmailReplyEnabled() {
      return this.isFeatureEnabledonAccount(
        this.accountId,
        FEATURE_FLAGS.QUOTED_EMAIL_REPLY
      );
    },
    quotedReplyPreference() {
      if (!this.isAnEmailChannel || !this.isQuotedEmailReplyEnabled) {
        return false;
      }

      return !!this.fetchQuotedReplyFlagFromUISettings(this.channelType);
    },
    lastEmailWithQuotedContent() {
      if (!this.isAnEmailChannel) {
        return null;
      }

      const lastEmail = this.lastEmail;
      if (!lastEmail || lastEmail.private) {
        return null;
      }

      return lastEmail;
    },
    quotedEmailText() {
      return extractQuotedEmailText(this.lastEmailWithQuotedContent);
    },
    quotedEmailPreviewText() {
      return truncatePreviewText(this.quotedEmailText, 80);
    },
    shouldShowQuotedReplyToggle() {
      return (
        this.isAnEmailChannel &&
        !this.isOnPrivateNote &&
        this.isQuotedEmailReplyEnabled
      );
    },
    shouldShowQuotedPreview() {
      return (
        this.shouldShowQuotedReplyToggle &&
        this.quotedReplyPreference &&
        !!this.quotedEmailText
      );
    },
    isDefaultEditorMode() {
      return !this.showAudioRecorderEditor && !this.copilot.isActive.value;
    },
    // M v3 · M1.B: ¿Otro agente está "atendiendo" esta conversación?
    // Devuelve true solo si:
    //   - existe attending_user_id en additional_attributes
    //   - NO está marcada como reclaimed (eso lo maneja isLockedByReclaim)
    //   - NO soy yo el atendedor
    //   - NO soy el responsable original (el responsable siempre puede responder)
    //   - el TTL de 30 min no ha expirado
    isAttendedByOtherAgent() {
      if (!this.currentUser?.id || !this.currentChat) return false;
      const additional = this.currentChat.additional_attributes || {};
      const attendingId = additional.attending_user_id;
      const attendingSince = additional.attending_since;
      if (!attendingId || !attendingSince) return false;
      if (additional.attending_reclaimed_at) return false;
      if (this.currentChat.meta?.assignee?.id === this.currentUser.id) return false;
      if (Number(attendingId) === this.currentUser.id) return false;
      const elapsed = Date.now() - Number(attendingSince) * 1000;
      return elapsed < 30 * 60 * 1000;
    },
    // M v5 · M1.B: ¿Soy el ex-atendedor y el responsable ya reclamó?
    // Bloquear permanentemente al ex-atendedor para evitar que siga respondiendo
    // después de que el responsable original retomó el control.
    isLockedByReclaim() {
      if (!this.currentUser?.id || !this.currentChat) return false;
      const additional = this.currentChat.additional_attributes || {};
      if (!additional.attending_reclaimed_at) return false;
      if (Number(additional.attending_user_id) !== this.currentUser.id) return false;
      // Si yo soy el responsable actual (raro pero posible si me reasignaron), permito
      if (this.currentChat.meta?.assignee?.id === this.currentUser.id) return false;
      return true;
    },
    attendingByName() {
      return this.currentChat?.additional_attributes?.attending_user_name || 'otro agente';
    },
    reclaimedByName() {
      return this.currentChat?.meta?.assignee?.available_name
        || this.currentChat?.meta?.assignee?.name
        || 'el responsable';
    },
    inboxDisconnected() {
      // Canal caído (Victor 2026-05-17 · redes 2026-05-25): el inbox de esta
      // conversación está desconectado → no escribir (el cliente no recibiría
      // el mensaje). WhatsApp/TikTok por conn_status; Instagram/Facebook por
      // reauthorization_required (Meta revocó el token).
      return isChannelDown(this.currentChat, this.inbox);
    },
    downBrand() {
      return networkBrand(this.inbox);
    },
    isEditorDisabled() {
      // M v3: bloquear input si la conv está siendo atendida por otro agente.
      // M v5: también bloquear si soy ex-atendedor y la conv fue reclamada.
      // Las notas privadas también se bloquean para evitar confusiones — el
      // atendedor y el responsable son los únicos canales formales de respuesta.
      if (this.inboxDisconnected) return true;
      if (this.isAttendedByOtherAgent) return true;
      if (this.isLockedByReclaim) return true;
      // Anti-choque vista compartida: bloquea responder al cliente · las
      // notas internas (modo privado) sí quedan habilitadas.
      if (this.isBlockedByConnectedView && !this.isOnPrivateNote) return true;
      return (
        (this.isAWhatsAppChannel || this.isAPIInbox) &&
        !this.isOnPrivateNote &&
        !this.currentChat.can_reply
      );
    },
  },
  watch: {
    currentChat(conversation, oldConversation) {
      const { can_reply: canReply } = conversation;
      if (oldConversation && oldConversation.id !== conversation.id) {
        // Only update email fields when switching to a completely different conversation (by ID)
        // This prevents overwriting user input (e.g., CC/BCC fields) when performing actions
        // like self-assign or other updates that do not actually change the conversation context
        this.setCCAndToEmailsFromLastChat();
        // Reset Copilot editor state (includes cancelling ongoing generation)
        this.copilot.reset();
      }

      if (this.isOnPrivateNote) {
        return;
      }

      // Anti-choque vista compartida: chat de otro agente → forzar Nota privada.
      if (this.isBlockedByConnectedView) {
        this.replyType = REPLY_EDITOR_MODES.NOTE;
      } else if (canReply || this.isAWhatsAppChannel || this.isAPIInbox) {
        this.replyType = REPLY_EDITOR_MODES.REPLY;
      } else {
        this.replyType = REPLY_EDITOR_MODES.NOTE;
      }

      this.fetchAndSetReplyTo();
    },
    // When moving from one conversation to another, the store may not have the
    // list of all the messages. A fetch is subsequently made to get the messages.
    // This watcher handles two main cases:
    // 1. When switching conversations and messages are fetched/updated, ensures CC/BCC fields are set from the latest OUTGOING/INCOMING email (not activity/private messages).
    // 2. Fixes and issue where CC/BCC fields could be reset/lost after assignment/activity actions or message mutations that did not represent a true email context change.
    lastEmail: {
      handler(lastEmail) {
        if (!lastEmail) return;
        this.setCCAndToEmailsFromLastChat();
      },
      deep: true,
    },
    conversationIdByRoute(conversationId, oldConversationId) {
      if (conversationId !== oldConversationId) {
        this.presentationHandledFor = null;
        this.presentationWarning = '';
        this.greetingEligible = false;
        this.dailyGreetingEligible = false;
        this.dailyGreetingEncargado = null;
        this.presentationRequired = false;
        this.presentationText = '';
        this.autoCorrectStats = { grammar: 0, accents: 0, caps: 0, total: 0 };
        this.fetchGreetingEligibility();
        this.fetchPhotographerPresentation();
        this.setToDraft(oldConversationId, this.replyType);
        this.getFromDraft();
        this.resetRecorderAndClearAttachments();
      }
    },
    message() {
      // Autosave the current message draft.
      this.doAutoSaveDraft();
      // Auto-corregir al pausar (Victor 2026-06-05): tras 1.5s sin teclear, la IA
      // corrige gramática/ortografía. doAutoCorrect es debounced (se define en mounted).
      if (this.autoCorrectEnabled && this.doAutoCorrect) this.doAutoCorrect();
      // Cliente nuevo · Fase 2 (Victor 2026-05-24): el saludo es OBLIGATORIO y
      // queda de SOLO LECTURA — no se puede editar ni borrar (ni agentes ni
      // admin), solo enviarlo con Enter. Cualquier cambio se revierte al texto
      // exacto. No aplica en personal ni a clientes que regresan (ahí
      // isNewClientConversation es false). El editor sigue enfocable, así que
      // Enter envía normalmente.
      if (
        this.isNewClientConversation &&
        this.replyType === REPLY_EDITOR_MODES.REPLY &&
        this.message !== this.presentationMessage
      ) {
        this.presentationWarning =
          'Saludo obligatorio: no se puede editar ni borrar. Envíalo con Enter.';
        this.message = this.presentationMessage;
        clearTimeout(this._presWarnTimer);
        this._presWarnTimer = setTimeout(() => {
          this.presentationWarning = '';
        }, 4500);
      }
    },
    // Auto-corrección de carrera: los mensajes cargan async. Si al cargar resulta
    // que el equipo SÍ había respondido y la presentación seguía intacta (sin
    // editar), la retira para no ensuciar chats con historial.
    teamHasReplied(replied) {
      // Si otro agente respondió, borramos el saludo precargado del cuadro (tanto
      // la presentación de cliente nuevo como el saludo del día) para no mandarlo
      // duplicado cuando dos agentes abren el mismo chat (Victor 2026-06-07).
      if (
        replied &&
        (this.message === this.presentationMessage ||
          this.message === this.dailyGreetingMessage)
      ) {
        this.message = '';
      }
    },
    replyType(updatedReplyType, oldReplyType) {
      this.setToDraft(this.conversationIdByRoute, oldReplyType);
      this.getFromDraft();
    },
  },

  mounted() {
    this.getFromDraft();
    this.fetchGreetingEligibility();
    this.fetchPhotographerPresentation();
    // Anti-choque vista compartida: si abro un chat ajeno, arranco en Nota privada.
    if (this.isBlockedByConnectedView) {
      this.replyType = REPLY_EDITOR_MODES.NOTE;
    }
    // Don't use the keyboard listener mixin here as the events here are supposed to be
    // working even if the editor is focussed.
    document.addEventListener('paste', this.onPaste);
    document.addEventListener('keydown', this.handleKeyEvents);
    this.setCCAndToEmailsFromLastChat();
    this.doAutoSaveDraft = debounce(
      () => {
        this.saveDraft(this.conversationIdByRoute, this.replyType);
      },
      500,
      true
    );
    // Auto-corregir al pausar: dispara la corrección 1.5s después del último tecleo.
    this.doAutoCorrect = debounce(() => {
      this.runAutoCorrect();
    }, 1500);

    this.fetchAndSetReplyTo();
    // onToggleReplyTo (no fetchAndSetReplyTo directo): cuando el usuario pulsa
    // "Responder a este mensaje", además de setear la cita lleva el cursor al
    // editor para escribir de una (Victor 2026-05-23).
    emitter.on(BUS_EVENTS.TOGGLE_REPLY_TO_MESSAGE, this.onToggleReplyTo);

    // A hacky fix to solve the drag and drop
    // Is showing on top of new conversation modal drag and drop
    // TODO need to find a better solution
    emitter.on(
      BUS_EVENTS.NEW_CONVERSATION_MODAL,
      this.onNewConversationModalActive
    );
    emitter.on(BUS_EVENTS.INSERT_INTO_NORMAL_EDITOR, this.addIntoEditor);
    emitter.on(CMD_AI_ASSIST, this.executeCopilotAction);
  },
  unmounted() {
    document.removeEventListener('paste', this.onPaste);
    document.removeEventListener('keydown', this.handleKeyEvents);
    emitter.off(BUS_EVENTS.TOGGLE_REPLY_TO_MESSAGE, this.onToggleReplyTo);
    emitter.off(BUS_EVENTS.INSERT_INTO_NORMAL_EDITOR, this.addIntoEditor);
    emitter.off(
      BUS_EVENTS.NEW_CONVERSATION_MODAL,
      this.onNewConversationModalActive
    );
    emitter.off(CMD_AI_ASSIST, this.executeCopilotAction);
  },
  methods: {
    getDraftKey(
      conversationId = this.conversationIdByRoute,
      replyType = this.replyType
    ) {
      return `draft-${conversationId}-${replyType}`;
    },
    getCopilotAcceptedMessage(replyType = this.replyType) {
      const key = this.getDraftKey(this.conversationIdByRoute, replyType);
      return this.copilotAcceptedMessages[key] || '';
    },
    setCopilotAcceptedMessage(message, replyType = this.replyType) {
      const key = this.getDraftKey(this.conversationIdByRoute, replyType);
      this.copilotAcceptedMessages[key] = trimContent(message || '');
    },
    clearCopilotAcceptedMessage(replyType = this.replyType) {
      const key = this.getDraftKey(this.conversationIdByRoute, replyType);
      delete this.copilotAcceptedMessages[key];
    },
    handleInsert(article) {
      const { url, title } = article;
      // Removing empty lines from the title
      const lines = title.split('\n');
      const nonEmptyLines = lines.filter(line => line.trim() !== '');
      const filteredMarkdown = nonEmptyLines.join(' ');
      emitter.emit(
        BUS_EVENTS.INSERT_INTO_RICH_EDITOR,
        `[${filteredMarkdown}](${url})`
      );

      useTrack(CONVERSATION_EVENTS.INSERT_ARTICLE_LINK);
    },
    toggleQuotedReply() {
      if (!this.isAnEmailChannel) {
        return;
      }

      const nextValue = !this.quotedReplyPreference;
      this.setQuotedReplyFlagForInbox(this.channelType, nextValue);
    },
    shouldIncludeQuotedEmail() {
      return (
        this.isQuotedEmailReplyEnabled &&
        this.quotedReplyPreference &&
        this.shouldShowQuotedReplyToggle &&
        !!this.quotedEmailText
      );
    },
    getMessageWithQuotedEmailText(message) {
      if (!this.shouldIncludeQuotedEmail()) {
        return message;
      }

      const quotedText = this.quotedEmailText || '';
      const header = buildQuotedEmailHeader(
        this.lastEmailWithQuotedContent,
        this.currentContact,
        this.inbox
      );

      return appendQuotedTextToMessage(message, quotedText, header);
    },
    resetRecorderAndClearAttachments() {
      // Reset audio recorder UI state
      this.resetAudioRecorderInput();
      // Reset attached files
      this.attachedFiles = [];
    },
    saveDraft(conversationId, replyType) {
      if (this.message || this.message === '') {
        const key = this.getDraftKey(conversationId, replyType);
        const draftToSave = trimContent(this.message || '');

        this.$store.dispatch('draftMessages/set', {
          key,
          message: draftToSave,
        });
      }
    },
    setToDraft(conversationId, replyType) {
      this.saveDraft(conversationId, replyType);
      this.message = '';
    },
    getFromDraft() {
      if (this.conversationIdByRoute) {
        const key = this.getDraftKey();
        const messageFromStore =
          this.$store.getters['draftMessages/get'](key) || '';

        // ensure that the message has signature set based on the ui setting
        this.message = this.toggleSignatureForDraft(messageFromStore);
        this.maybePrefillPresentation();
      }
    },
    // Pregunta al backend si esta conversación es de cliente NUEVO en la sucursal
    // (y no es un número personal). Al resolver, intenta pre-cargar el saludo.
    fetchGreetingEligibility() {
      const id = this.conversationIdByRoute;
      if (!id) return;
      // Super admins no reciben saludos auto-precargados (presentación de cliente
      // nuevo ni saludo del día) — Victor 2026-06-06.
      if (this.isSuperAdmin) {
        this.greetingEligible = false;
        this.dailyGreetingEligible = false;
        this.dailyGreetingEncargado = null;
        return;
      }
      ConversationApi.newClientGreeting(id)
        .then(({ data }) => {
          // Evita pisar si el agente ya cambió de conversación.
          if (this.conversationIdByRoute !== id) return;
          this.greetingEligible = !!data?.eligible;
          this.dailyGreetingEligible = !!data?.daily_greeting;
          this.dailyGreetingEncargado = data?.daily_greeting_encargado || null;
          // Presentación de cliente nuevo tiene prioridad; si no aplica, se
          // intenta el saludo del día (ambas guardan en cuadro vacío).
          this.maybePrefillPresentation();
          this.maybePrefillDailyGreeting();
        })
        .catch(() => {
          this.greetingEligible = false;
          this.dailyGreetingEligible = false;
          this.dailyGreetingEncargado = null;
        });
    },
    // Cliente nuevo + modo respuesta + cuadro vacío → pre-carga la presentación.
    maybePrefillPresentation() {
      if (
        this.isNewClientConversation &&
        this.replyType === REPLY_EDITOR_MODES.REPLY &&
        !this.message.trim()
      ) {
        this.message = this.presentationMessage;
      }
    },
    // Primer mensaje del día (cliente nuevo o viejo) + modo respuesta + cuadro
    // vacío → pre-carga el saludo del día con el nombre del agente (Propuesta 1,
    // editable). No aplica si ya va la presentación de cliente nuevo.
    maybePrefillDailyGreeting() {
      if (
        this.dailyGreetingEligible &&
        !this.isNewClientConversation &&
        this.replyType === REPLY_EDITOR_MODES.REPLY &&
        !this.isOnPrivateNote &&
        !this.message.trim()
      ) {
        this.message = this.dailyGreetingMessage;
      }
    },
    // Presentación obligatoria Fotógrafo / Video (Victor 2026-06-07): pregunta al
    // backend si este usuario es fotógrafo Y el cliente aún no fue presentado. Si
    // sí, guarda el texto fijo y muestra la tarjeta que bloquea el cuadro.
    fetchPhotographerPresentation() {
      const id = this.conversationIdByRoute;
      if (!id) return;
      ConversationApi.photographerPresentation(id)
        .then(({ data }) => {
          if (this.conversationIdByRoute !== id) return;
          this.presentationRequired = !!data?.required;
          this.presentationText = data?.message || '';
        })
        .catch(() => {
          this.presentationRequired = false;
          this.presentationText = '';
        });
    },
    // Envía el saludo fijo y sella al cliente. Al terminar, libera el cuadro.
    sendPhotographerPresentation() {
      const id = this.conversationIdByRoute;
      if (!id || this.presentationSending) return;
      this.presentationSending = true;
      ConversationApi.sendPhotographerPresentation(id)
        .then(() => {
          this.presentationRequired = false;
          useAlert('Presentación enviada ✓ — ya puedes chatear con el cliente.');
        })
        .catch(() => {
          useAlert('No se pudo enviar la presentación. Intenta de nuevo.');
        })
        .finally(() => {
          this.presentationSending = false;
        });
    },
    toggleSignatureForDraft(message) {
      if (this.isPrivate) {
        return message;
      }

      const effectiveChannelType = getEffectiveChannelType(
        this.channelType,
        this.inbox?.medium || ''
      );
      return this.sendWithSignature
        ? appendSignature(message, this.messageSignature, effectiveChannelType)
        : removeSignature(message, this.messageSignature, effectiveChannelType);
    },
    removeFromDraft() {
      if (this.conversationIdByRoute) {
        const key = this.getDraftKey();
        this.$store.dispatch('draftMessages/delete', { key });
      }
    },
    getElementToBind() {
      return this.replyEditor;
    },
    getKeyboardEvents() {
      return {
        Escape: {
          action: () => {
            this.hideEmojiPicker();
          },
          allowOnFocusedInput: true,
        },
        '$mod+KeyK': {
          action: e => {
            e.preventDefault();
            const ninja = document.querySelector('ninja-keys');
            ninja.open();
          },
          allowOnFocusedInput: true,
        },
        Enter: {
          action: e => {
            if (this.isAValidEvent('enter')) {
              this.onSendReply();
              e.preventDefault();
            }
          },
          allowOnFocusedInput: true,
        },
        '$mod+Enter': {
          action: () => {
            if (this.copilot.isActive.value && this.isFocused) {
              this.onSubmitCopilotReply();
            } else if (this.isAValidEvent('cmd_enter')) {
              this.onSendReply();
            }
          },
          allowOnFocusedInput: true,
        },
      };
    },
    isAValidEvent(selectedKey) {
      return (
        !this.showUserMentions &&
        !this.showMentions &&
        !this.showCannedMenu &&
        !this.showVariablesMenu &&
        this.isFocused &&
        this.isEditorHotKeyEnabled(selectedKey)
      );
    },
    onPaste(e) {
      // Don't handle paste if compose new conversation modal is open
      if (this.newConversationModalActive) return;

      // Don't handle paste if editor is disabled
      if (this.isEditorDisabled) return;

      // Filter valid files (non-zero size)
      Array.from(e.clipboardData.files)
        .filter(file => file.size > 0)
        .filter(file => {
          const isAllowed = isFileTypeAllowedForChannel(file, {
            channelType: this.channelType || this.inbox?.channel_type,
            medium: this.inbox?.medium,
            conversationType: this.conversationType,
            isInstagramChannel: this.isAnInstagramChannel,
            isOnPrivateNote: this.isOnPrivateNote,
          });

          if (!isAllowed) {
            useAlert(
              this.$t('CONVERSATION.FILE_TYPE_NOT_SUPPORTED', {
                fileName: file.name,
              })
            );
          }

          return isAllowed;
        })
        .forEach(file => {
          const { name, type, size } = file;
          this.onFileUpload({ name, type, size, file });
        });
    },
    toggleUserMention(currentMentionState) {
      this.showUserMentions = currentMentionState;
    },
    toggleCannedMenu(value) {
      this.showCannedMenu = value;
    },
    toggleVariablesMenu(value) {
      this.showVariablesMenu = value;
    },
    openWhatsappTemplateModal() {
      this.showWhatsAppTemplatesModal = true;
    },
    hideWhatsappTemplatesModal() {
      this.showWhatsAppTemplatesModal = false;
    },
    openContentTemplateModal() {
      this.showContentTemplatesModal = true;
    },
    hideContentTemplatesModal() {
      this.showContentTemplatesModal = false;
    },
    confirmOnSendReply() {
      if (this.isReplyButtonDisabled) {
        return;
      }
      if (!this.showMentions) {
        const copilotAcceptedMessage = this.getCopilotAcceptedMessage();
        const isOnWhatsApp =
          this.isATwilioWhatsAppChannel ||
          this.isAWhatsAppCloudChannel ||
          this.is360DialogWhatsAppChannel;
        // Instagram and TikTok do not support sending text and attachments in the same message.
        // For Instagram, combining them causes duplicate messages due to separate echo events per component.
        // For TikTok, the API rejects messages that mix text and media.
        // To handle both cases, text and attachments are always sent as separate messages.
        const isOnInstagram = this.isAnInstagramChannel;
        const isOnTiktok = this.isATiktokChannel;
        if ((isOnWhatsApp || isOnInstagram || isOnTiktok) && !this.isPrivate) {
          this.sendMessageAsMultipleMessages(
            this.message,
            copilotAcceptedMessage
          );
        } else {
          const messagePayload = this.getMessagePayload(this.message);
          this.sendMessage(
            messagePayload,
            this.message,
            copilotAcceptedMessage
          );
        }

        if (!this.isPrivate) {
          this.clearEmailField();
        }

        this.clearMessage();
        this.hideEmojiPicker();
        this.$emit('update:popOutReplyBox', false);
      }
    },
    sendMessageAsMultipleMessages(message, copilotAcceptedMessage = '') {
      const messages = this.getMultipleMessagesPayload(message);
      messages.forEach(messagePayload => {
        this.sendMessage(
          messagePayload,
          messagePayload.message || '',
          copilotAcceptedMessage
        );
      });
    },
    sendMessageAnalyticsData(
      isPrivate,
      { editorMessage = '', copilotAcceptedMessage = '' } = {}
    ) {
      const normalizeForComparison = message => {
        let normalizedMessage = message || '';

        if (this.sendWithSignature && this.messageSignature && !isPrivate) {
          const effectiveChannelType = getEffectiveChannelType(
            this.channelType,
            this.inbox?.medium || ''
          );
          normalizedMessage = removeSignature(
            normalizedMessage,
            this.messageSignature,
            effectiveChannelType
          );
        }

        return trimContent(normalizedMessage);
      };

      const normalizedAcceptedMessage = normalizeForComparison(
        copilotAcceptedMessage
      );
      const normalizedEditorMessage = normalizeForComparison(editorMessage);

      if (normalizedAcceptedMessage && normalizedEditorMessage) {
        useTrack(CAPTAIN_EVENTS.AI_ASSISTED_MESSAGE_SENT, {
          conversationId: this.conversationIdByRoute,
          channelType: this.channelType,
          editedBeforeSend:
            normalizedAcceptedMessage !== normalizedEditorMessage,
          isPrivate,
        });
      }

      // Analytics data for message signature is enabled or not in channels
      return isPrivate
        ? useTrack(CONVERSATION_EVENTS.SENT_PRIVATE_NOTE)
        : useTrack(CONVERSATION_EVENTS.SENT_MESSAGE, {
            channelType: this.channelType,
            signatureEnabled: this.sendWithSignature,
            hasReplyTo: !!this.inReplyTo?.id,
          });
    },
    async onSendReply() {
      // Presentación obligatoria Fotógrafo / Video: mientras la tarjeta esté
      // arriba, no se puede mandar nada al cliente (ni con Enter) hasta enviar el
      // saludo fijo desde el botón de la tarjeta (Victor 2026-06-07).
      if (this.presentationRequired) return;
      // Al enviar a un cliente nuevo, marca la presentación como manejada para
      // que el cuadro NO se vuelva a rellenar tras el envío.
      if (this.isNewClientConversation && this.message.trim()) {
        this.presentationHandledFor = this.conversationIdByRoute;
      }
      const undefinedVariables = getUndefinedVariablesInMessage({
        message: this.message,
        variables: this.messageVariables,
      });
      if (undefinedVariables.length > 0) {
        const undefinedVariablesCount =
          undefinedVariables.length > 1 ? undefinedVariables.length : 1;
        this.undefinedVariableMessage = this.$t(
          'CONVERSATION.REPLYBOX.UNDEFINED_VARIABLES.MESSAGE',
          {
            undefinedVariablesCount,
            undefinedVariables: undefinedVariables.join(', '),
          }
        );

        const ok = await this.$refs.confirmDialog.showConfirmation();
        if (ok) {
          this.confirmOnSendReply();
        }
      } else {
        this.confirmOnSendReply();
      }
    },
    async sendMessage(
      messagePayload,
      editorMessage = '',
      copilotAcceptedMessage = ''
    ) {
      // @mención → tarea (Victor 2026-06-09): captura la tarea ANTES del envío
      // (la nota se limpia justo después). Si la nota menciona agentes y el
      // usuario puede asignar, se crea una tarea por agente al confirmarse.
      const mentionTask = this.collectMentionTask(messagePayload);
      try {
        await this.$store.dispatch(
          'createPendingMessageAndSend',
          messagePayload
        );
        emitter.emit(BUS_EVENTS.SCROLL_TO_MESSAGE);
        emitter.emit(BUS_EVENTS.MESSAGE_SENT);
        this.removeFromDraft();
        if (mentionTask) this.createMentionTasks(mentionTask);
        this.sendMessageAnalyticsData(messagePayload.private, {
          editorMessage,
          copilotAcceptedMessage,
        });
      } catch (error) {
        const errorMessage =
          error?.response?.data?.error || this.$t('CONVERSATION.MESSAGE_ERROR');
        useAlert(errorMessage);
      }
    },
    // @mención → tarea (Victor 2026-06-09) ───────────────────────────
    // Reúne la tarea a crear si la nota privada menciona agentes y el usuario
    // está autorizado. Devuelve null si no aplica. Se llama de forma síncrona
    // ANTES de limpiar la nota, para no perder el contenido ni la prioridad.
    collectMentionTask(messagePayload) {
      if (!messagePayload?.private || !this.canAssignMentionTask) return null;
      const content = messagePayload.message || '';
      const assigneeIds = [
        ...new Set(
          [...content.matchAll(/mention:\/\/user\/(\d+)/g)].map(m =>
            Number(m[1])
          )
        ),
      ];
      if (!assigneeIds.length) return null;
      // Nombres mencionados (para el aviso) y texto legible (sin el markdown).
      const names = [
        ...content.matchAll(/\[([^\]]+)\]\(mention:\/\/user\/\d+\//g),
      ].map(m => m[1].replace(/^@/, ''));
      const readable = content
        .replace(/\[([^\]]+)\]\(mention:\/\/[^)]+\)/g, '$1')
        .replace(/\s+/g, ' ')
        .trim();
      return {
        assigneeIds,
        names: [...new Set(names)],
        title: readable.slice(0, 120) || 'Tarea asignada en nota',
        description: readable.slice(0, 5000),
        priority: this.mentionTaskPriority,
        dueDate: this.mentionDueDateISO(this.mentionTaskDueKey),
      };
    },
    // Vencimiento de la tarjeta (hoy/mañana/3 días) → fin de ese día en ISO.
    mentionDueDateISO(key) {
      const d = new Date();
      if (key === 'manana') d.setDate(d.getDate() + 1);
      else if (key === '3dias') d.setDate(d.getDate() + 3);
      d.setHours(23, 59, 0, 0);
      return d.toISOString();
    },
    // Crea una tarea por cada agente mencionado, ligada a este cliente.
    async createMentionTasks({
      assigneeIds,
      names,
      title,
      description,
      priority,
      dueDate,
    }) {
      try {
        await Promise.all(
          assigneeIds.map(assigneeId =>
            TasksAPI.create({
              assignee_id: assigneeId,
              conversation_id: this.currentChat.id,
              title,
              description,
              priority,
              due_date: dueDate,
            })
          )
        );
        const who = names.length ? names.join(', ') : 'el agente';
        useAlert(`✅ Tarea asignada a ${who}`);
      } catch (error) {
        useAlert(
          error?.response?.data?.error ||
            'No se pudo crear la tarea de la mención'
        );
      }
      // Restablece la tarjeta para la próxima nota.
      this.mentionTaskDueKey = 'hoy';
      this.mentionTaskPriority = 'normal';
    },
    async onSendWhatsAppReply(messagePayload) {
      this.sendMessage({
        conversationId: this.currentChat.id,
        ...messagePayload,
      });
      this.hideWhatsappTemplatesModal();
    },
    async onSendContentTemplateReply(messagePayload) {
      this.sendMessage({
        conversationId: this.currentChat.id,
        ...messagePayload,
      });
      this.hideContentTemplatesModal();
    },
    replaceText(message) {
      if (this.sendWithSignature && !this.private) {
        // if signature is enabled, append it to the message
        // appendSignature ensures that the signature is not duplicated
        // so we don't need to check if the signature is already present
        const effectiveChannelType = getEffectiveChannelType(
          this.channelType,
          this.inbox?.medium || ''
        );
        message = appendSignature(
          message,
          this.messageSignature,
          effectiveChannelType
        );
      }

      const updatedMessage = replaceVariablesInMessage({
        message,
        variables: this.messageVariables,
      });

      setTimeout(() => {
        useTrack(CONVERSATION_EVENTS.INSERTED_A_CANNED_RESPONSE);
        this.message = updatedMessage;
      }, 100);
    },
    // Plantillas de Mensajes (Victor 2026-05-23): resuelve variables {español} con
    // el contexto del chat y coloca el texto en el cuadro (el agente revisa/edita).
    resolveTemplateBody(body) {
      const contact =
        this.$store.getters['contacts/getContact'](
          this.currentChat?.meta?.sender?.id
        ) || {};
      const branch = (this.$store.getters['branches/getBranches'] || []).find(
        b => b.id === this.currentChat?.branch_id
      );
      const map = {
        nombre_cliente: contact.name || '',
        nombre_agente: this.currentUser?.name || '',
        sucursal: branch?.name || branch?.code || '',
      };
      return (body || '').replace(/\{(\w+)\}/g, (m, k) =>
        k in map && map[k] ? map[k] : m
      );
    },
    onTemplateSelect(template) {
      this.replaceText(this.resolveTemplateBody(template.body));
      this.showTemplatePanel = false;
      // Auditoría de uso (Fase 3 · no bloquea el chat si falla)
      MessageTemplatesAPI.recordUsage(template.id, {
        conversation_id: this.currentChat?.id,
        contact_id: this.currentChat?.meta?.sender?.id,
        branch_id: this.currentChat?.branch_id,
        channel: this.channelType,
      }).catch(() => {});
    },
    setReplyMode(mode = REPLY_EDITOR_MODES.REPLY) {
      // Anti-choque vista compartida: si el chat lo atiende otro agente, solo
      // se permite el modo Nota privada (no responder al cliente).
      if (this.isBlockedByConnectedView) {
        mode = REPLY_EDITOR_MODES.NOTE;
      }
      // Clear attachments when switching between private note and reply modes
      // This is to prevent from breaking the upload rules
      if (this.attachedFiles.length > 0) this.attachedFiles = [];

      const { can_reply: canReply } = this.currentChat;
      this.$store.dispatch('draftMessages/setReplyEditorMode', {
        mode,
      });
      if (canReply || this.isAWhatsAppChannel || this.isAPIInbox)
        this.replyType = mode;
      if (this.isRecordingAudio) {
        this.toggleAudioRecorder();
      }
    },
    clearEditorSelection() {
      this.updateEditorSelectionWith = '';
    },
    addIntoEditor(content) {
      this.updateEditorSelectionWith = content;
      this.onFocus();
    },
    executeCopilotAction(action, data) {
      // V5-P1 · 2026-05-15 (Victor) · auto-aplicar sin panel azul ni clic Accept
      this.copilot.execute(action, data, content => {
        this.message = content;
        this.setCopilotAcceptedMessage(content);
      });
    },
    // Auto-corregir al pausar (Victor 2026-06-05): corrige gramática/ortografía solo,
    // 1.5s tras dejar de teclear. Candados:
    //  - solo si el interruptor está activo
    //  - no toca el saludo obligatorio de cliente nuevo (solo lectura)
    //  - ignora textos muy cortos o sin letras (ej. "6" de los menús de bot)
    //  - no re-corrige el mismo resultado (anti-loop)
    //  - si el agente siguió escribiendo mientras la IA pensaba, NO pisa su texto
    runAutoCorrect() {
      if (!this.autoCorrectEnabled) return;
      if (
        this.isNewClientConversation &&
        this.replyType === REPLY_EDITOR_MODES.REPLY
      )
        return;
      const sent = this.message || '';
      const text = sent.trim();
      if (text.length < 4) return;
      if (!/[a-záéíóúñü]/i.test(text)) return;
      if (sent === this.lastAutoCorrected) return;
      this.copilot.execute('fix_spelling_grammar', sent, content => {
        if (!content || this.message !== sent) return;
        this.lastAutoCorrected = content;
        if (content !== sent) {
          this.autoCorrectStats = this.classifyCorrections(sent, content);
          this.message = content;
          this.setCopilotAcceptedMessage(content);
        } else {
          this.autoCorrectStats = { grammar: 0, accents: 0, caps: 0, total: 0 };
        }
      });
    },
    // Clasifica las diferencias entre el texto original y el corregido en
    // mayúsculas / tildes / gramática, comparando palabra por palabra (Victor
    // 2026-06-05 · HUD P4). Aproximado pero suficiente para el indicador.
    classifyCorrections(before, after) {
      const wa = String(before).trim().split(/\s+/);
      const wb = String(after).trim().split(/\s+/);
      const strip = s => s.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
      let grammar = 0;
      let accents = 0;
      let caps = 0;
      const n = Math.max(wa.length, wb.length);
      for (let i = 0; i < n; i += 1) {
        const a = wa[i] || '';
        const b = wb[i] || '';
        if (a === b) continue; // eslint-disable-line no-continue
        if (a.toLowerCase() === b.toLowerCase()) caps += 1;
        else if (strip(a).toLowerCase() === strip(b).toLowerCase()) accents += 1;
        else grammar += 1;
      }
      return { grammar, accents, caps, total: grammar + accents + caps };
    },
    clearMessage() {
      this.message = '';
      this.autoCorrectStats = { grammar: 0, accents: 0, caps: 0, total: 0 };
      this.clearCopilotAcceptedMessage();
      if (this.sendWithSignature && !this.isPrivate) {
        // if signature is enabled, append it to the message
        const effectiveChannelType = getEffectiveChannelType(
          this.channelType,
          this.inbox?.medium || ''
        );
        this.message = appendSignature(
          this.message,
          this.messageSignature,
          effectiveChannelType
        );
      }
      this.attachedFiles = [];
      this.isRecordingAudio = false;
      this.resetReplyToMessage();
      this.resetAudioRecorderInput();
    },
    clearEmailField() {
      this.ccEmails = '';
      this.bccEmails = '';
      this.toEmails = '';
    },

    toggleEmojiPicker() {
      this.showEmojiPicker = !this.showEmojiPicker;
    },
    toggleAudioRecorder() {
      this.isRecordingAudio = !this.isRecordingAudio;
      if (!this.isRecordingAudio) {
        this.resetAudioRecorderInput();
      }
    },
    toggleAudioRecorderPlayPause() {
      if (!this.$refs.audioRecorderInput) return;
      if (!this.recordingAudioState) {
        this.$refs.audioRecorderInput.stopRecording();
      } else {
        this.$refs.audioRecorderInput.playPause();
      }
    },
    hideEmojiPicker() {
      if (this.showEmojiPicker) {
        this.toggleEmojiPicker();
      }
    },
    onTypingOn() {
      this.toggleTyping('on');
    },
    onTypingOff() {
      this.toggleTyping('off');
    },
    onBlur() {
      this.isFocused = false;
      this.saveDraft(this.conversationIdByRoute, this.replyType);
    },
    onFocus() {
      this.isFocused = true;
      // Celular (Victor 2026-06-02): al tocar la barra para escribir, sube al
      // mensaje más reciente para no perder la conversación en tiempo real.
      if (this.isMobile) {
        emitter.emit(BUS_EVENTS.SCROLL_TO_MESSAGE);
      }
    },
    onRecordProgressChanged(duration) {
      this.recordingAudioDurationText = duration;
    },
    onFinishRecorder(file) {
      this.recordingAudioState = 'stopped';
      this.hasRecordedAudio = true;
      // Added a new key isRecordedAudio to the file to find it's and recorded audio
      // Because to filter and show only non recorded audio and other attachments
      const autoRecordedFile = {
        ...file,
        isRecordedAudio: true,
      };
      // Propuesta 2 · "un toque graba→envía": el envío se dispara DENTRO de
      // attachFile, cuando el audio YA quedó adjunto. Antes se llamaba aquí con
      // $nextTick, pero onFileUpload es async (FileReader/DirectUpload) y el
      // mensaje salía SIN el audio (carrera · Victor 2026-06-03). El flag
      // this.sendAfterRecord queda activo y attachFile lo consume.
      return file && this.onFileUpload(autoRecordedFile);
    },
    // Notas de voz Propuesta 2 · controles por clic (cel/laptop/PC)
    pauseResumeAudioRecording() {
      if (this.$refs.audioRecorderInput) {
        this.$refs.audioRecorderInput.pauseResume();
      }
    },
    cancelAudioRecording() {
      if (this.$refs.audioRecorderInput) {
        this.$refs.audioRecorderInput.cancelRecording();
      }
      this.isAudioPaused = false;
      this.resetAudioRecorderInput();
    },
    sendAudioRecording() {
      // OJO: NO usar isReplyButtonDisabled aquí — mientras grabas el mensaje está
      // vacío y aún no hay adjunto, así que ese guard da true y bloqueaba el envío
      // de la nota de voz en TODOS los dispositivos (Victor 2026-06-04). Solo
      // bloqueamos si el editor está realmente deshabilitado (p. ej. fuera de la
      // ventana de 24h de WhatsApp).
      if (this.isEditorDisabled) return;
      // Si ya se detuvo (archivo adjunto), enviar directo; si sigue grabando,
      // detener y enviar al terminar (un toque).
      if (this.hasRecordedAudio) {
        this.onSendReply();
      } else if (this.$refs.audioRecorderInput) {
        this.sendAfterRecord = true;
        this.$refs.audioRecorderInput.stopRecording();
      }
    },
    toggleTyping(status) {
      const conversationId = this.currentChat.id;
      const isPrivate = this.isPrivate;

      if (!conversationId) {
        return;
      }

      this.$store.dispatch('conversationTypingStatus/toggleTyping', {
        status,
        conversationId,
        isPrivate,
      });
    },
    attachFile({ blob, file }) {
      const reader = new FileReader();
      reader.readAsDataURL(file.file);
      reader.onloadend = () => {
        this.attachedFiles.push({
          currentChatId: this.currentChat.id,
          resource: blob || file,
          isPrivate: this.isPrivate,
          thumb: reader.result,
          blobSignedId: blob ? blob.signed_id : undefined,
          isRecordedAudio: file?.isRecordedAudio || false,
        });
        // Propuesta 2: si el usuario tocó ➤ mientras grababa, ahora que el audio
        // YA quedó adjunto lo enviamos (un solo toque graba→envía · PC y celular
        // · Victor 2026-06-03). Sin esto la nota de voz no se enviaba.
        if (this.sendAfterRecord && file?.isRecordedAudio) {
          this.sendAfterRecord = false;
          this.$nextTick(() => this.onSendReply());
          return;
        }
        // Enfoca el editor tras adjuntar para poder enviar el archivo con Enter
        // sin tener que clicar antes en "Escribe aquí tu mensaje" (Victor
        // 2026-05-23). Usa el método real del editor + un pequeño retardo para
        // ganarle al diálogo de archivos del SO que devuelve el foco al cerrar.
        const focusComposer = () => {
          const ed = this.$refs.messageEditorRef;
          if (ed?.focusEditorInputField) {
            ed.focusEditorInputField('end');
          } else {
            this.$refs.replyEditor?.querySelector('.ProseMirror')?.focus();
          }
          this.isFocused = true;
        };
        this.$nextTick(focusComposer);
        setTimeout(focusComposer, 150);
      };
    },
    removeAttachment(attachments) {
      this.attachedFiles = attachments;
    },
    setReplyToInPayload(payload) {
      if (this.inReplyTo?.id) {
        return {
          ...payload,
          contentAttributes: {
            ...payload.contentAttributes,
            in_reply_to: this.inReplyTo.id,
          },
        };
      }

      return payload;
    },
    getMultipleMessagesPayload(message) {
      const multipleMessagePayload = [];

      if (this.attachedFiles && this.attachedFiles.length) {
        let caption =
          this.isAnInstagramChannel || this.isATiktokChannel ? '' : message;
        this.attachedFiles.forEach(attachment => {
          const attachedFile = this.globalConfig.directUploadsEnabled
            ? attachment.blobSignedId
            : attachment.resource.file;
          let attachmentPayload = {
            conversationId: this.currentChat.id,
            files: [attachedFile],
            private: false,
            message: caption,
            sender: this.sender,
          };

          attachmentPayload = this.setReplyToInPayload(attachmentPayload);
          multipleMessagePayload.push(attachmentPayload);
          // For WhatsApp, only the first attachment gets a caption
          if (!this.isAnInstagramChannel) caption = '';
        });
      }

      const hasNoAttachments =
        !this.attachedFiles || !this.attachedFiles.length;
      // For Instagram and TikTok, text must always be sent as a separate message (no captions on attachments).
      // For WhatsApp, we only need a text message if there are no attachments.
      if (
        ((this.isAnInstagramChannel || this.isATiktokChannel) &&
          this.message) ||
        (!(this.isAnInstagramChannel || this.isATiktokChannel) &&
          hasNoAttachments)
      ) {
        let messagePayload = {
          conversationId: this.currentChat.id,
          message,
          private: false,
          sender: this.sender,
        };

        messagePayload = this.setReplyToInPayload(messagePayload);

        multipleMessagePayload.push(messagePayload);
      }

      return multipleMessagePayload;
    },
    getMessagePayload(message) {
      const messageWithQuote = this.getMessageWithQuotedEmailText(message);

      let messagePayload = {
        conversationId: this.currentChat.id,
        message: messageWithQuote,
        private: this.isPrivate,
        sender: this.sender,
      };
      messagePayload = this.setReplyToInPayload(messagePayload);

      if (this.attachedFiles && this.attachedFiles.length) {
        messagePayload.files = [];
        this.attachedFiles.forEach(attachment => {
          if (this.globalConfig.directUploadsEnabled) {
            messagePayload.files.push(attachment.blobSignedId);
          } else {
            messagePayload.files.push(attachment.resource.file);
          }
        });
      }

      if (this.ccEmails && !this.isOnPrivateNote) {
        messagePayload.ccEmails = this.ccEmails;
      }

      if (this.bccEmails && !this.isOnPrivateNote) {
        messagePayload.bccEmails = this.bccEmails;
      }

      if (this.toEmails && !this.isOnPrivateNote) {
        messagePayload.toEmails = this.toEmails;
      }
      return messagePayload;
    },
    setCcEmails(value) {
      this.bccEmails = value.bccEmails;
      this.ccEmails = value.ccEmails;
    },
    setCCAndToEmailsFromLastChat() {
      const conversationContact = this.currentChat?.meta?.sender?.email || '';
      const { email: inboxEmail, forward_to_email: forwardToEmail } =
        this.inbox;

      const { cc, bcc, to } = getRecipients(
        this.lastEmail,
        conversationContact,
        inboxEmail,
        forwardToEmail
      );

      this.toEmails = to.join(', ');
      this.ccEmails = cc.join(', ');
      this.bccEmails = bcc.join(', ');
    },
    fetchAndSetReplyTo() {
      const replyStorageKey = LOCAL_STORAGE_KEYS.MESSAGE_REPLY_TO;
      const replyToMessageId = LocalStorage.getFromJsonStore(
        replyStorageKey,
        this.conversationId
      );

      this.inReplyTo = this.currentChat?.messages?.find(message => {
        if (message.id === replyToMessageId) {
          return true;
        }
        return false;
      });
    },
    // Disparado por "Responder a este mensaje": setea la cita Y lleva el cursor
    // al editor para escribir directo (no se usa en el montaje, así no roba foco
    // al abrir un chat).
    onToggleReplyTo() {
      this.fetchAndSetReplyTo();
      this.$nextTick(() => {
        const editorEl = this.$refs.replyEditor?.querySelector('.ProseMirror');
        if (editorEl) editorEl.focus();
      });
    },
    resetReplyToMessage() {
      const replyStorageKey = LOCAL_STORAGE_KEYS.MESSAGE_REPLY_TO;
      LocalStorage.deleteFromJsonStore(replyStorageKey, this.conversationId);
      emitter.emit(BUS_EVENTS.TOGGLE_REPLY_TO_MESSAGE);
    },
    onNewConversationModalActive(isActive) {
      // Issue is if the new conversation modal is open and we drag and drop the file
      // then the file is not getting attached to the new conversation modal
      // and it is getting attached to the current conversation reply box
      // so to fix this we are removing the drag and drop event listener from the current conversation reply box
      // When new conversation modal is open
      this.newConversationModalActive = isActive;
    },
    onSearchPopoverClose() {
      this.showArticleSearchPopover = false;
    },
    toggleInsertArticle() {
      this.showArticleSearchPopover = !this.showArticleSearchPopover;
    },
    resetAudioRecorderInput() {
      this.recordingAudioDurationText = '00:00';
      this.isRecordingAudio = false;
      this.recordingAudioState = '';
      this.isAudioPaused = false;
      this.sendAfterRecord = false;
      this.hasRecordedAudio = false;
      // Only clear the recorded audio when we click toggle button.
      this.attachedFiles = this.attachedFiles.filter(
        file => !file?.isRecordedAudio
      );
    },
    togglePopout() {
      this.$emit('update:popOutReplyBox', !this.popOutReplyBox);
    },
    onSubmitCopilotReply() {
      const acceptedMessage = this.copilot.accept();
      this.message = acceptedMessage;
      this.setCopilotAcceptedMessage(acceptedMessage);
    },
  },
};
</script>

<template>
  <ReplyBoxBanner :message="message" :is-on-private-note="isOnPrivateNote" />
  <div v-if="isAttendedByOtherAgent" class="attend-lock-banner">
    <div class="attend-lock-banner__icon">🙋</div>
    <div class="attend-lock-banner__text">
      <b>{{ attendingByName }}</b> está atendiendo esta conversación.
      Solo el atendedor o el responsable original pueden responder al cliente.
    </div>
  </div>
  <div v-else-if="isLockedByReclaim" class="attend-lock-banner attend-lock-banner--reclaimed">
    <div class="attend-lock-banner__icon">🙋</div>
    <div class="attend-lock-banner__text">
      <b>{{ reclaimedByName }}</b> retomó la conversación. Gracias por tu apoyo · ya no puedes responder al cliente.
    </div>
  </div>
  <div ref="replyEditor" class="reply-box" :class="replyBoxClass">
    <!-- Presentación OBLIGATORIA del rol Fotógrafo / Video (Victor 2026-06-07):
         tarjeta que cubre el cuadro y bloquea el chat hasta enviar el saludo fijo.
         Una sola vez por cliente (el sello vive en el contacto, sobrevive a
         resolver/reabrir). -->
    <div
      v-if="presentationRequired"
      class="absolute inset-0 z-20 flex items-center justify-center p-3 rounded-[20px]"
      style="background: rgba(8,8,10,0.86); backdrop-filter: blur(3px);"
    >
      <div
        class="w-full max-w-[440px] rounded-2xl p-4"
        style="background: linear-gradient(180deg,#151517,#0e0e10); border: 1px solid rgba(201,163,90,0.5);"
      >
        <div class="flex items-center gap-2 text-[13px] font-extrabold" style="color:#d8bd86">
          🎬 Preséntate con el cliente
        </div>
        <div class="mt-1 text-[11px] leading-snug" style="color:#8b8b91">
          Solo la primera vez. Tu saludo se enviará una sola vez — aunque el chat se
          cierre y se reabra, no se repetirá.
        </div>
        <div
          class="flex items-center gap-2 mt-3 px-3 py-2 rounded-lg text-[12px]"
          style="background:#0c0c0e; border:1px solid rgba(255,255,255,0.08);"
        >
          <span style="color:#6c6c72">Agente</span>
          <span class="ml-auto font-bold" style="color:#e9e9ec">
            {{ currentUser?.available_name || currentUser?.name }} 🎬
          </span>
        </div>
        <div
          class="flex items-center gap-2 mt-2 px-3 py-2 rounded-lg text-[12px]"
          style="background:#0c0c0e; border:1px solid rgba(255,255,255,0.08);"
        >
          <span style="color:#6c6c72">Departamento</span>
          <span class="ml-auto font-bold" style="color:#e9e9ec">Producción de Video</span>
        </div>
        <div
          class="mt-3 px-3 py-2.5 rounded-xl text-[12px] leading-relaxed"
          style="background:#0c0c0e; border:1px dashed rgba(201,163,90,0.5); color:#dcdce0;"
        >
          {{ presentationText }}
        </div>
        <button
          class="w-full mt-3 flex items-center justify-center gap-2 rounded-xl py-3 text-[13px] font-extrabold disabled:opacity-60"
          style="background: linear-gradient(180deg,#d8bd86,#c9a35a); color:#1a1407;"
          :disabled="presentationSending"
          @click="sendPhotographerPresentation"
        >
          <span v-if="presentationSending">Enviando…</span>
          <span v-else>Enviar presentación ✓</span>
        </button>
      </div>
    </div>
    <MessageTemplatePanel
      v-if="showTemplatePanel"
      :branch-id="currentChat?.branch_id"
      @select="onTemplateSelect"
      @close="showTemplatePanel = false"
    />
    <div
      v-if="presentationWarning"
      class="flex items-center gap-2 px-4 py-2.5 text-[12.5px] font-bold border-t"
      style="background: rgba(255,91,91,0.12); border-color: rgba(255,91,91,0.4); color: #ff9d9d;"
    >
      ⚠️ {{ presentationWarning }}
    </div>
    <div
      v-if="inboxDisconnected"
      class="flex items-center gap-3 px-4 py-3 border-t border-[rgba(255,91,91,0.4)]"
      style="background: linear-gradient(90deg, rgba(255,91,91,0.12), rgba(255,91,91,0.03));"
    >
      <span
        class="flex items-center justify-center w-8 h-8 rounded-lg text-base shrink-0"
        style="background: rgba(255,91,91,0.18); border: 1px solid rgba(255,91,91,0.4);"
      >⚠️</span>
      <div class="flex-1 min-w-0">
        <div class="text-[13px] font-bold" style="color:#ff9d9d">
          {{ downBrand.label }} desconectado — no puedes escribir
        </div>
        <div class="text-[11.5px]" style="color:#9a9db0">
          El cliente NO recibiría el mensaje. Comunícate con el encargado. Se reactiva al reconectar.
        </div>
      </div>
    </div>
    <!-- Cinta superior modo Nota · Victor 2026-05-30 (P1): banner discreto que
         identifica el contexto "nota privada solo agentes" sin invadir el área
         del editor. Reemplaza el borde amarillo grueso anterior por una franja
         elegante con icono y leyenda. -->
    <div v-if="isOnPrivateNote" class="reply-box__note-banner">
      <span class="reply-box__note-banner-lock">🔒</span>
      <span class="reply-box__note-banner-label">Nota privada</span>
      <span class="reply-box__note-banner-sub">· solo visible para agentes</span>
      <span class="reply-box__note-banner-shield">🛡</span>
    </div>
    <!-- @mención → tarea (Victor 2026-06-09): al mencionar un agente en la nota,
         esta tarjeta deja elegir vencimiento + prioridad. Al enviar se crea una
         tarea para ese agente, ligada a este cliente. Solo para los 5 autorizados. -->
    <div v-if="showMentionTaskCard" class="mtask-card">
      <div class="mtask-head">
        <span class="mtask-ic">📋</span>
        <span
          >Tarea para
          <strong>{{ mentionedAgentNames.join(', ') }}</strong></span
        >
      </div>
      <div class="mtask-row">
        <span class="mtask-lbl">Vence:</span>
        <button
          type="button"
          class="mtask-chip"
          :class="{ on: mentionTaskDueKey === 'hoy' }"
          @click="mentionTaskDueKey = 'hoy'"
        >Hoy</button>
        <button
          type="button"
          class="mtask-chip"
          :class="{ on: mentionTaskDueKey === 'manana' }"
          @click="mentionTaskDueKey = 'manana'"
        >Mañana</button>
        <button
          type="button"
          class="mtask-chip"
          :class="{ on: mentionTaskDueKey === '3dias' }"
          @click="mentionTaskDueKey = '3dias'"
        >3 días</button>
      </div>
      <div class="mtask-row">
        <span class="mtask-lbl">Prioridad:</span>
        <button
          type="button"
          class="mtask-chip"
          :class="{ on: mentionTaskPriority === 'normal' }"
          @click="mentionTaskPriority = 'normal'"
        >Normal</button>
        <button
          type="button"
          class="mtask-chip"
          :class="{ on: mentionTaskPriority === 'alta' }"
          @click="mentionTaskPriority = 'alta'"
        >Alta 🔔</button>
        <button
          type="button"
          class="mtask-chip"
          :class="{ on: mentionTaskPriority === 'urgente' }"
          @click="mentionTaskPriority = 'urgente'"
        >Urgente 🚨</button>
      </div>
    </div>
    <!-- HUD de auto-corrección (Victor 2026-06-05 · P4): sin línea divisoria. Si la
         IA corrigió algo, muestra un anillo dorado con el total + chips por tipo
         (Aa gramática · ´ tildes · ⇧ mayúsculas). Si no, solo "✦ Auto-corrección". -->
    <div
      v-if="autoCorrectEnabled && !isOnPrivateNote"
      class="flex items-center justify-end gap-2 px-3 py-1"
    >
      <template v-if="autoCorrectStats.total > 0">
        <span
          class="grid w-[26px] h-[26px] rounded-full place-items-center text-[10px] font-extrabold text-amber-400 border-2 border-amber-500/70 shadow-[0_0_8px_rgba(201,163,90,0.35)]"
          title="Correcciones de la IA en este mensaje"
          >{{ autoCorrectStats.total }}</span
        >
        <span
          v-if="autoCorrectStats.grammar"
          v-tooltip.top="'Gramática / ortografía'"
          class="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-emerald-500/[0.12] text-emerald-300 border border-emerald-500/25"
          >Aa<span v-if="autoCorrectStats.grammar > 1" class="opacity-70">×{{ autoCorrectStats.grammar }}</span></span
        >
        <span
          v-if="autoCorrectStats.accents"
          v-tooltip.top="'Tildes'"
          class="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-emerald-500/[0.12] text-emerald-300 border border-emerald-500/25"
          >´<span v-if="autoCorrectStats.accents > 1" class="opacity-70">×{{ autoCorrectStats.accents }}</span></span
        >
        <span
          v-if="autoCorrectStats.caps"
          v-tooltip.top="'Mayúsculas'"
          class="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-emerald-500/[0.12] text-emerald-300 border border-emerald-500/25"
          >⇧<span v-if="autoCorrectStats.caps > 1" class="opacity-70">×{{ autoCorrectStats.caps }}</span></span
        >
      </template>
      <span
        v-else
        class="inline-flex items-center gap-1.5 text-[11px] font-medium text-amber-500"
        ><span>✦</span><span>Auto-corrección activa</span></span
      >
    </div>
    <ReplyTopPanel
      :mode="replyType"
      :conversation-id="conversationId"
      :is-reply-restricted="isReplyRestricted"
      :disabled="
        (copilot.isActive.value && copilot.isButtonDisabled.value) ||
        showAudioRecorderEditor
      "
      :is-editor-disabled="isEditorDisabled"
      :is-message-length-reaching-threshold="isMessageLengthReachingThreshold"
      :characters-remaining="charactersRemaining"
      :editor-content="message"
      :popout-reply-box="popOutReplyBox"
      @set-reply-mode="setReplyMode"
      @toggle-popout="togglePopout"
      @toggle-copilot="copilot.toggleEditor"
      @execute-copilot-action="executeCopilotAction"
    />
    <ConversationViewersBanner />
    <ArticleSearchPopover
      v-if="showArticleSearchPopover && connectedPortalSlug"
      :selected-portal-slug="connectedPortalSlug"
      @insert="handleInsert"
      @close="onSearchPopoverClose"
    />
    <Transition
      mode="out-in"
      enter-active-class="transition-all duration-300 ease-out"
      enter-from-class="opacity-0 translate-y-2 scale-[0.98]"
      enter-to-class="opacity-100 translate-y-0 scale-100"
      leave-active-class="transition-all duration-200 ease-in"
      leave-from-class="opacity-100 translate-y-0 scale-100"
      leave-to-class="opacity-0 translate-y-2 scale-[0.98]"
    >
      <div :key="copilot.editorTransitionKey.value" class="reply-box__top">
        <ReplyToMessage
          v-if="shouldShowReplyToMessage"
          :message="inReplyTo"
          @dismiss="resetReplyToMessage"
        />
        <EmojiInput
          v-if="showEmojiPicker"
          v-on-clickaway="hideEmojiPicker"
          :class="{
            'emoji-dialog--expanded': isOnExpandedLayout || popOutReplyBox,
          }"
          :on-click="addIntoEditor"
        />
        <ReplyEmailHead
          v-if="showReplyHead && isDefaultEditorMode"
          v-model:cc-emails="ccEmails"
          v-model:bcc-emails="bccEmails"
          v-model:to-emails="toEmails"
        />
        <!-- Notas de voz · barra de grabación Propuesta 2 (Victor 2026-06-02):
             🗑 cancela · 🔴 tiempo · onda dorada · ⏸ pausa/reanuda · ➤ enviar.
             Controles por clic → iguales en celular, laptop y PC. -->
        <div v-if="showAudioRecorderEditor" class="vg-rec-bar">
          <button
            type="button"
            class="vg-rec-btn vg-rec-trash"
            aria-label="Cancelar nota de voz"
            @click="cancelAudioRecording"
          >
            <span class="i-ph-trash" />
          </button>
          <span class="vg-rec-dot" :class="{ 'is-paused': isAudioPaused }" />
          <span class="vg-rec-time">{{ recordingAudioDurationText }}</span>
          <AudioRecorder
            ref="audioRecorderInput"
            class="vg-rec-wave"
            :audio-record-format="audioRecordFormat"
            @recorder-progress-changed="onRecordProgressChanged"
            @finish-record="onFinishRecorder"
            @pause-state-changed="isAudioPaused = $event"
          />
          <button
            type="button"
            class="vg-rec-btn vg-rec-pause"
            :aria-label="isAudioPaused ? 'Reanudar' : 'Pausar'"
            @click="pauseResumeAudioRecording"
          >
            <span :class="isAudioPaused ? 'i-ph-play-fill' : 'i-ph-pause-fill'" />
          </button>
          <button
            type="button"
            class="vg-rec-send"
            :disabled="isEditorDisabled"
            aria-label="Enviar nota de voz"
            @click="sendAudioRecording"
          >
            <fluent-icon icon="send" size="15" />
          </button>
        </div>
        <CopilotEditorSection
          v-if="copilot.isActive.value && !showAudioRecorderEditor"
          :show-copilot-editor="copilot.showEditor.value"
          :is-generating-content="copilot.isGenerating.value"
          :generated-content="copilot.generatedContent.value"
          :is-popout="popOutReplyBox"
          :placeholder="$t('CONVERSATION.FOOTER.COPILOT_MSG_INPUT')"
          @focus="onFocus"
          @blur="onBlur"
          @clear-selection="clearEditorSelection"
          @close="copilot.showEditor.value = false"
          @content-ready="copilot.setContentReady"
          @send="copilot.sendFollowUp"
        />
        <ReplyBottomPanel
          v-else-if="isDefaultEditorMode"
          key="reply-bottom-panel-inline"
          :conversation-id="conversationId"
          :enable-multiple-file-upload="enableMultipleFileUpload"
          :enable-whats-app-templates="showWhatsappTemplates"
          :enable-content-templates="showContentTemplates"
          :inbox="inbox"
          :is-on-private-note="isOnPrivateNote"
          :is-recording-audio="isRecordingAudio"
          :is-send-disabled="isReplyButtonDisabled"
          :is-note="isPrivate"
          :is-editor-disabled="isEditorDisabled"
          :on-file-upload="onFileUpload"
          :on-send="onSendReply"
          :conversation-type="conversationType"
          :recording-audio-duration-text="recordingAudioDurationText"
          :recording-audio-state="recordingAudioState"
          :send-button-text="replyButtonLabel"
          :show-audio-recorder="showAudioRecorder"
          :show-emoji-picker="showEmojiPicker"
          :show-file-upload="showFileUpload"
          :show-quoted-reply-toggle="shouldShowQuotedReplyToggle"
          :quoted-reply-enabled="quotedReplyPreference"
          :toggle-audio-recorder-play-pause="toggleAudioRecorderPlayPause"
          :toggle-audio-recorder="toggleAudioRecorder"
          :toggle-emoji-picker="toggleEmojiPicker"
          :message="message"
          :portal-slug="connectedPortalSlug"
          :new-conversation-modal-active="newConversationModalActive"
          @select-whatsapp-template="openWhatsappTemplateModal"
          @select-content-template="openContentTemplateModal"
          @replace-text="replaceText"
          @toggle-insert-article="toggleInsertArticle"
          @toggle-quoted-reply="toggleQuotedReply"
          @toggle-templates="showTemplatePanel = !showTemplatePanel"
        >
          <template #editor>
            <WootMessageEditor
              ref="messageEditorRef"
              v-model="message"
              :conversation-id="conversationId"
              :editor-id="editorStateId"
              class="input popover-prosemirror-menu"
              :is-private="isOnPrivateNote"
              :placeholder="messagePlaceHolder"
              :update-selection-with="updateEditorSelectionWith"
              :min-height="4"
              :disabled="isEditorDisabled"
              :focus-on-mount="!isMobile"
              enable-variables
              :variables="messageVariables"
              :signature="messageSignature"
              allow-signature
              :channel-type="channelType"
              :medium="inbox.medium"
              @typing-off="onTypingOff"
              @typing-on="onTypingOn"
              @focus="onFocus"
              @blur="onBlur"
              @toggle-user-mention="toggleUserMention"
              @toggle-canned-menu="toggleCannedMenu"
              @toggle-variables-menu="toggleVariablesMenu"
              @clear-selection="clearEditorSelection"
              @execute-copilot-action="executeCopilotAction"
            />
          </template>
        </ReplyBottomPanel>

        <QuotedEmailPreview
          v-if="shouldShowQuotedPreview && isDefaultEditorMode"
          :quoted-email-text="quotedEmailText"
          :preview-text="quotedEmailPreviewText"
          class="mb-2"
          @toggle="toggleQuotedReply"
        />

        <div
          v-if="hasAttachments && isDefaultEditorMode"
          class="bg-transparent py-0 mb-2"
          @paste="onPaste"
        >
          <AttachmentPreview
            class="mt-2"
            :attachments="attachedFiles"
            @remove-attachment="removeAttachment"
          />
        </div>
        <MessageSignatureMissingAlert
          v-if="
            isSignatureEnabledForInbox &&
            !isSignatureAvailable &&
            isDefaultEditorMode
          "
          class="mb-2"
        />
      </div>
    </Transition>

    <Transition
      mode="out-in"
      enter-active-class="transition-all duration-300 ease-out"
      enter-from-class="opacity-0 translate-y-2 scale-[0.98]"
      enter-to-class="opacity-100 translate-y-0 scale-100"
      leave-active-class="transition-all duration-200 ease-in"
      leave-from-class="opacity-100 translate-y-0 scale-100"
      leave-to-class="opacity-0 translate-y-2 scale-[0.98]"
    >
      <!-- V5-P1 · 2026-05-15 (Victor) · panel oculto · usamos auto-apply silencioso -->
      <CopilotReplyBottomPanel
        v-if="false"
        key="copilot-bottom-panel"
        :is-generating-content="copilot.isButtonDisabled.value"
        @submit="onSubmitCopilotReply"
        @cancel="copilot.reset"
      />
    </Transition>

    <WhatsappTemplates
      :inbox-id="inbox.id"
      :show="showWhatsAppTemplatesModal"
      @close="hideWhatsappTemplatesModal"
      @on-send="onSendWhatsAppReply"
      @cancel="hideWhatsappTemplatesModal"
    />

    <ContentTemplates
      :inbox-id="inbox.id"
      :show="showContentTemplatesModal"
      @close="hideContentTemplatesModal"
      @on-send="onSendContentTemplateReply"
      @cancel="hideContentTemplatesModal"
    />

    <woot-confirm-modal
      ref="confirmDialog"
      :title="$t('CONVERSATION.REPLYBOX.UNDEFINED_VARIABLES.TITLE')"
      :description="undefinedVariableMessage"
    />
  </div>
</template>

<style lang="scss" scoped>
.send-button {
  @apply mb-0;
}

/* === Notas de voz · barra de grabación Propuesta 2 (Victor 2026-06-02) ===
   🗑 cancelar · 🔴 tiempo · onda dorada · ⏸ pausa/reanuda · ➤ enviar.
   Botones por clic → idénticos en celular, laptop y PC. */
.vg-rec-bar {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 10px;
}
.vg-rec-btn {
  flex: none;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.05);
  color: rgba(255, 255, 255, 0.7);
  border: 0;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;

  span {
    width: 18px;
    height: 18px;
  }
  &:hover {
    background: rgba(255, 255, 255, 0.1);
    color: #fff;
  }
}
.vg-rec-trash {
  color: #e5484d;
  &:hover {
    background: rgba(229, 72, 77, 0.14);
    color: #ff5d62;
  }
}
.vg-rec-pause {
  color: #c9a35a;
}
.vg-rec-dot {
  flex: none;
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: #e5484d;
  box-shadow: 0 0 8px rgba(229, 72, 77, 0.8);
  animation: vgRecPulse 1.4s ease-in-out infinite;

  &.is-paused {
    background: rgba(255, 255, 255, 0.35);
    box-shadow: none;
    animation: none;
  }
}
.vg-rec-time {
  flex: none;
  min-width: 38px;
  font-size: 13px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  color: #e7e9ec;
}
.vg-rec-wave {
  flex: 1 1 auto;
  min-width: 0;
}
.vg-rec-send {
  flex: none;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #fbbf24, #d97706);
  color: #1a1209;
  border: 0;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(251, 191, 36, 0.3);
  transition: transform 0.12s ease, box-shadow 0.15s ease, opacity 0.15s ease;

  &:hover:not(:disabled) {
    transform: scale(1.05);
    box-shadow: 0 3px 12px rgba(251, 191, 36, 0.45);
  }
  &:active:not(:disabled) {
    transform: scale(0.95);
  }
  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
    box-shadow: none;
  }
}
@keyframes vgRecPulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.35;
  }
}

/* === R4 · ReplyBox pill (M1.B, WhatsApp/iMessage style, ~80px altura) === */
.reply-box {
  @apply relative mb-2 mx-2;
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.08);
  transition: border-color 0.18s ease, background 0.18s ease;
  overflow: visible;

  &:focus-within {
    border-color: rgba(251, 191, 36, 0.40);
    background: rgba(255, 255, 255, 0.04);
  }

  &.is-private {
    background: rgba(251, 191, 36, 0.025);
    border-color: rgba(251, 191, 36, 0.22);
  }
  &.is-private:focus-within {
    border-color: rgba(251, 191, 36, 0.45);
  }
}

/* Banner P1 · cinta superior modo Nota privada (Victor 2026-05-30) */
.reply-box__note-banner {
  display: flex;
  align-items: center;
  gap: 8px;
  background: linear-gradient(90deg, rgba(251,191,36,0.14), rgba(251,191,36,0.04));
  border-bottom: 1px solid rgba(251,191,36,0.30);
  border-top-left-radius: 19px;
  border-top-right-radius: 19px;
  padding: 7px 14px;
  font-size: 11px;
  color: #fbbf24;
  font-weight: 700;
  letter-spacing: 0.2px;
  line-height: 1.3;
}
.reply-box__note-banner-lock { font-size: 11px; }
.reply-box__note-banner-label { font-weight: 700; }
.reply-box__note-banner-sub {
  color: rgba(251,191,36,0.60);
  font-weight: 500;
  font-size: 10px;
  margin-left: 2px;
  letter-spacing: 0.2px;
}
.reply-box__note-banner-shield {
  margin-left: auto;
  font-size: 10.5px;
  opacity: 0.60;
}

/* @mención → tarea (Victor 2026-06-09): tarjeta dorada para fijar vencimiento y
   prioridad de la tarea que se crea al mencionar a un agente en una nota. */
.mtask-card {
  background: linear-gradient(180deg, rgba(201,163,90,0.10), rgba(201,163,90,0.04));
  border: 1px solid rgba(201,163,90,0.32);
  border-radius: 12px;
  padding: 9px 12px;
  margin: 6px 8px 2px;
  display: flex;
  flex-direction: column;
  gap: 7px;
}
.mtask-head {
  display: flex;
  align-items: center;
  gap: 7px;
  font-size: 12px;
  color: #e7cf9a;
  font-weight: 600;
}
.mtask-head strong { color: #e7cf9a; font-weight: 800; }
.mtask-ic { font-size: 13px; }
.mtask-row { display: flex; align-items: center; gap: 6px; flex-wrap: wrap; }
.mtask-lbl {
  font-size: 10px;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  color: rgba(255,255,255,0.5);
  margin-right: 2px;
}
.mtask-chip {
  font-size: 11px;
  font-weight: 600;
  color: rgba(255,255,255,0.7);
  background: rgba(255,255,255,0.04);
  border: 1px solid rgba(255,255,255,0.10);
  border-radius: 999px;
  padding: 4px 11px;
  cursor: pointer;
  transition: all 0.15s;
}
.mtask-chip:hover:not(.on) {
  color: #e7cf9a;
  background: rgba(201,163,90,0.10);
  border-color: rgba(201,163,90,0.30);
}
.mtask-chip.on {
  color: #e7cf9a;
  background: rgba(201,163,90,0.18);
  border-color: rgba(201,163,90,0.55);
  font-weight: 800;
}

.send-button {
  @apply mb-0;
}

.reply-box__top {
  @apply relative;
  padding: 0 4px;
  margin-top: -1px;
}

/* En modo default el inline pill no necesita padding extra de reply-box__top */
.reply-box__top :deep(.reply-bottom-panel-r4.is-inline-pill) {
  background: transparent;
  border-radius: 18px;
  margin: 2px 0;
}

.emoji-dialog {
  @apply top-[unset] -bottom-10 ltr:-left-80 ltr:right-[unset] rtl:left-[unset] rtl:-right-80;

  &::before {
    filter: drop-shadow(0px 4px 4px rgba(0, 0, 0, 0.08));
    @apply ltr:-right-4 bottom-2 rtl:-left-4 ltr:rotate-[270deg] rtl:rotate-[90deg];
  }
}

.emoji-dialog--expanded {
  @apply left-[unset] bottom-0 absolute z-[100];

  &::before {
    transform: rotate(0deg);
    @apply ltr:left-1 rtl:right-1 -bottom-2;
  }
}

/* Banner de bloqueo cuando otro agente está atendiendo (M v3 · M1.B) */
.attend-lock-banner {
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 8px 14px 0;
  padding: 10px 14px;
  background: linear-gradient(180deg, rgba(139,92,246,0.14), rgba(139,92,246,0.04));
  border: 1px solid rgba(139,92,246,0.4);
  border-radius: 10px;
  position: relative;
  overflow: hidden;
  box-shadow: inset 0 1px 0 rgba(139,92,246,0.15);
}
.attend-lock-banner::before {
  content: '';
  position: absolute;
  top: 0; left: 0; right: 0; height: 1px;
  background: linear-gradient(90deg, transparent, rgba(196,181,253,0.7), transparent);
  pointer-events: none;
}
.attend-lock-banner__icon {
  width: 32px; height: 32px;
  border-radius: 50%;
  background: linear-gradient(135deg, #8b5cf6, #6d28d9);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  flex-shrink: 0;
  box-shadow: 0 3px 8px rgba(139,92,246,0.35);
}
.attend-lock-banner__text {
  flex: 1;
  font-size: 12px;
  color: rgba(255,255,255,0.85);
  line-height: 1.45;
}
.attend-lock-banner__text b {
  color: #c4b5fd;
  font-weight: 800;
}
</style>
